import commercialPrintApi from '@/api/v1/commercialPrint';
import router from '@/router/index';
import utils from '@/helpers/utils';

const initialCommercialPrint = {
  id: '',
  name: '',
  description: '',
  createdAt: '',
  deadline: '',
  commercialPrintType: '',
  status: '',
  members: [],
  orderers: [],
  managers: [],
  files: [],
  discLocation: '',
  printCoordination: false,
  printCoordinationEditable: false,
  history: {
    changes: [],
    total: 0,
  },
};

export default {
  namespaced: true,

  state: {
    commercialPrint: initialCommercialPrint,
    commercialPrintChanges: [],
    actions: [],
    isProcessing: false,
  },

  mutations: {
    SET_SINGLE_COMMERCIAL_PRINT(state, commercialPrintData) {
      state.commercialPrint = commercialPrintData;
    },

    SET_COMMERCIAL_PRINT_CHANGES(state, changes) {
      state.commercialPrintChanges = changes;
    },

    CHANGE_PROCESSING_STATUS(state, status) {
      state.isProcessing = status;
    },

    SET_COMMERCIAL_PRINT_ACTIONS(state, actions) {
      state.actions = actions;
    },

    CLEAR_COMMERCIAL_PRINT_DATA(state) {
      state.commercialPrint = initialCommercialPrint;
    },

    ADD_FILE(state, file) {
      state.commercialPrint.files.push(file);
    },

    DELETE_COMMERCIAL_PRINT_USER(state, userData) {
      state.commercialPrint[userData.listType] = state.commercialPrint[
        userData.listType
      ].filter((user) => user.id !== userData.id);
    },
  },

  actions: {
    fetchSingleCommercialPrint({ commit }, id) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });

      return new Promise((resolve) => {
        commercialPrintApi
          .show(id)
          .then((resp) => {
            commit('SET_SINGLE_COMMERCIAL_PRINT', resp.data);
            resolve(resp.data);
          })
          .finally(() => {
            commit('CHANGE_LOADING_STATUS', false, { root: true });
          });
      });
    },

    fetchCommercialPrintChanges({ commit, rootState }, id) {
      const { selectedFilters: filters, order } = rootState.filters;

      return new Promise((resolve) => {
        commercialPrintApi.history
          .show(id, { filters, order })
          .then((response) => {
            commit('SET_COMMERCIAL_PRINT_CHANGES', response.data);
            resolve();
          });
      });
    },

    createCommercialPrint({ commit, dispatch }, commercialPrintData) {
      commit('CHANGE_PROCESSING_STATUS', true);

      const commercialPrintFormData = new FormData();
      utils.buildFormData(commercialPrintFormData, commercialPrintData);

      commercialPrintApi
        .create(commercialPrintFormData)
        .then((resp) => {
          const successMessage = 'Utworzono Akcydens NBP';

          dispatch(
            'snackbar/showSnackbar',
            {
              message: [successMessage],
              type: 'success',
            },
            { root: true }
          );
          commit('CHANGE_PROCESSING_STATUS', false);
          router.push({
            name: 'commercial-print-index',
            params: { startTab: 'applications-list' },
          });
        })
        .catch(() => {
          commit('CHANGE_PROCESSING_STATUS', false);
        });
    },

    fetchCommercialPrintActions({ commit }, commercialPrintId) {
      return new Promise((resolve) => {
        commercialPrintApi.actions.show(commercialPrintId).then((response) => {
          commit('SET_COMMERCIAL_PRINT_ACTIONS', response.data);
          resolve();
        });
      });
    },

    downloadCommercialPrintFiles({ commit }, fileIds) {
      commit('CHANGE_PROCESSING_STATUS', true);

      return commercialPrintApi.files
        .downloadFiles(fileIds)
        .then((resp) => {
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(
            new Blob([resp.data], { type: 'application/zip' })
          );
          link.setAttribute(
            'download',
            `Pliki Akcydens NBP [${fileIds.length}]`
          );
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        })
        .finally(() => {
          commit('CHANGE_PROCESSING_STATUS', false);
        });
    },

    deleteCommercialPrintFiles({ commit, dispatch, state }, fileIds) {
      commit('CHANGE_PROCESSING_STATUS', true);

      return new Promise((resolve) => {
        commercialPrintApi.files
          .delete(state.commercialPrint.id, fileIds)
          .then(() => {
            dispatch(
              'snackbar/showSnackbar',
              {
                message: ['Plik(i) zostały usunięte'],
                type: 'success',
              },
              { root: true }
            );
            dispatch('fetchSingleCommercialPrint', state.commercialPrint.id);
            resolve();
          })
          .finally(() => {
            commit('CHANGE_PROCESSING_STATUS', false);
          });
      });
    },

    uploadCommercialPrintFile({ commit, dispatch, state }, fileFormData) {
      return new Promise((resolve, reject) => {
        commercialPrintApi.files
          .upload(state.commercialPrint.id, fileFormData)
          .then((resp) => {
            dispatch(
              'snackbar/showSnackbar',
              {
                message: ['Plik został dodany'],
                type: 'success',
              },
              { root: true }
            );
            commit('ADD_FILE', resp.data);
            resolve();
          })
          .catch(() => {
            reject();
          });
      });
    },

    makeCommercialPrintAction({ commit, dispatch, state }, actionData) {
      dispatch('changeActionStatus', true, { root: true });

      commercialPrintApi.actions
        .set(state.commercialPrint.id, actionData)
        .then((response) => {
          dispatch('dialog/clearDialog', null, { root: true });

          dispatch(
            'snackbar/showSnackbar',
            {
              message: ['Akcja wykonana'],
              type: 'success',
            },
            { root: true }
          );

          commit('SET_COMMERCIAL_PRINT_ACTIONS', response.data);
          dispatch('fetchSingleCommercialPrint', state.commercialPrint.id);
        })
        .finally(() => {
          dispatch('changeActionStatus', false, { root: true });
        });
    },

    deleteCommercialPrint({ commit, dispatch }, id) {
      commit('CHANGE_LOADING_STATUS', true, { root: true });

      return commercialPrintApi
        .delete(id)
        .then(() => {
          dispatch(
            'snackbar/showSnackbar',
            {
              message: ['Akcydens został usunięty'],
              type: 'success',
            },
            { root: true }
          );
        })
        .finally(() => {
          commit('CHANGE_LOADING_STATUS', false, { root: true });
        });
    },

    updateProject({ commit, dispatch, state }, projectData) {
      commit('CHANGE_PROCESSING_STATUS', true);

      return commercialPrintApi
        .update(state.commercialPrint.id, projectData)
        .then((response) => {
          commit('SET_SINGLE_COMMERCIAL_PRINT', response.data);
          dispatch(
            'snackbar/showSnackbar',
            {
              message: ['Akcydens NBP zaktualizowany'],
              type: 'success',
            },
            { root: true }
          );
          router.push({
            name: 'commercial-print-single',
            params: { id: state.commercialPrint.id },
          });
        })
        .finally(() => {
          commit('CHANGE_PROCESSING_STATUS', false);
        });
    },

    createCommercialPrintUser({ commit, dispatch, state }, userData) {
      commit('CHANGE_USER_PROCESSING_STATUS', true);

      return new Promise((resolve) => {
        commercialPrintApi.commercialPrints
          .createUser({
            commercialPrintId: state.commercialPrint.id,
            ...userData,
          })
          .then((response) => {
            dispatch(
              'snackbar/showSnackbar',
              {
                message: ['Użytkownik został dodany'],
                type: 'success',
              },
              { root: true }
            );

            dispatch('fetchSingleCommercialPrint', state.commercialPrint.id);
            resolve();
          })
          .finally(() => {
            commit('CHANGE_USER_PROCESSING_STATUS', false);
          });
      });
    },

    deleteCommercialPrintUser({ commit, dispatch }, userData) {
      commercialPrintApi.commercialPrints
        .deleteUser(userData.id, userData.listType)
        .then(() => {
          dispatch(
            'snackbar/showSnackbar',
            {
              message: ['Użytkownik został usunięty'],
              type: 'success',
            },
            { root: true }
          );

          commit('DELETE_COMMERCIAL_PRINT_USER', userData);
        })
        .catch((err) => {
          dispatch(
            'snackbar/showSnackbar',
            {
              message: err.response.data.errors,
              type: 'error',
            },
            { root: true }
          );
        });
    },

    changeProcessingStatus({ commit }, status) {
      commit('CHANGE_PROCESSING_STATUS', status);
    },

    resetCommercialPrint({ commit, dispatch }) {
      commit('CLEAR_COMMERCIAL_PRINT_DATA');
      commit('SET_COMMERCIAL_PRINT_ACTIONS', []);
      dispatch('changeActionStatus', false, { root: true });
    },
  },
};
